import { useEffect, useRef, type ReactNode } from 'react';
import { Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

// Shown when a request fails for a system reason (non-2xx, WAF block, network error). The raw
// error never reaches the user: a status code or backend string means nothing to them.
export const SYSTEM_ERROR_TITLE = 'Unable to continue';
export const SYSTEM_ERROR_MESSAGE = 'Sorry something went wrong, please try again later';

export interface FormAlertState {
  title: string;
  message: ReactNode;
}

export const systemErrorAlert = (): FormAlertState => ({
  title: SYSTEM_ERROR_TITLE,
  message: SYSTEM_ERROR_MESSAGE,
});

// FormAlert renders a form-level error above the form and scrolls it into view each time a new
// alert is set, so users who submitted from the bottom of a long page see why they can't continue.
export default function FormAlert({ alert }: { alert?: FormAlertState }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (alert) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [alert]);

  return (
    <div ref={ref}>
      {alert && (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle />}
          title={alert.title}
          styles={{ title: { fontWeight: 700 } }}
        >
          {alert.message}
        </Alert>
      )}
    </div>
  );
}
